<div
    class="app-user-form"
    id="userForm"
    *ngIf="userForm"
    [ngClass]="isEditMode ? 'editing' : isCreateMode ? 'creating' : 'viewing'"
    [formGroup]="userForm"
>
    <!--- CREATE --->
    <div *ngIf="isCreateMode">
        <mat-stepper
            [linear]="!isUserCreated()"
            #stepper
            orientation="vertical"
            [animationDuration]="500"
            (selectionChange)="onStepChange($event)"
            class="stepper-container"
            [ngClass]="{ 'disabled-steps': !isUserCreated() }"
        >
            <!-- Icon overrides. -->
            <ng-template matStepperIcon="number" let-index="index">
                <app-stepper-icon
                    [index]="index"
                    [active]="selectedIndex === index"
                    [completed]="hasSaved && index < selectedIndex"
                ></app-stepper-icon>
            </ng-template>
            <ng-template matStepperIcon="edit" let-index="index">
                <app-stepper-icon
                    [index]="index"
                    [active]="selectedIndex === index"
                    [completed]="hasSaved && index < selectedIndex"
                ></app-stepper-icon>
            </ng-template>
            <ng-template matStepperIcon="done" let-index="index">
                <app-stepper-icon
                    [index]="index"
                    [active]="selectedIndex === index"
                    [completed]="hasSaved && index < selectedIndex"
                ></app-stepper-icon>
            </ng-template>
            <!-- Step 1: Identification -->
            <mat-step
                label="{{ 'user.stepper.identification' | translate }}"
                [disabled]="userSubmitService.submittingUser$ | async"
                [stepControl]="identificationGroup"
            >
                <div class="form-group reduced-width mb-0" data-automation="identificationSection">
                    <div class="form-entry" *ngIf="isInternal">
                        <!-- Enel Employee ID -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.enel_employee_id' | translate }}</mat-label>
                            <input
                                matInput
                                id="enel_employee_id"
                                type="text"
                                class="input-entry"
                                data-automation="enel_employee_id"
                                placeholder="{{ 'user.create.placeholder.enel_employee_id' | translate }}"
                                maxlength="100"
                                formControlName="enel_employee_id"
                                data-url-persist
                            />
                            <mat-error
                                *ngIf="
                                    userForm.get('enel_employee_id').invalid &&
                                    (userForm.get('enel_employee_id').dirty || userForm.get('enel_employee_id').touched)
                                "
                            >
                                <mat-error *ngIf="userForm.get('enel_employee_id').errors?.required">
                                    <span data-automation="enelEmployeeIdErrorRequired">{{
                                        'user.validation.required' | translate
                                    }}</span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('enel_employee_id').errors?.pattern">
                                    <span>{{ 'user.validation.pattern' | translate }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>
                    <div class="form-entry">
                        <!-- First Name -->
                        <multi-locale-input
                            formControlName="first_names"
                            [inputLabel]="firstnameLabel"
                            [locales]="multiLocaleConfig.supportedLocales"
                            [displayLabelKey]="multiLocaleConfig.displayLabelKey"
                            [localeKey]="multiLocaleConfig.localeKey"
                            [defaultLocale]="multiLocaleConfig.defaultLocale"
                            [placeholderText]="firstnamePlaceholder"
                            [required]="true"
                            [id]="'user_first_name'"
                            data-automation="firstName"
                        >
                        </multi-locale-input>
                    </div>

                    <div class="form-entry">
                        <!-- Last Name -->
                        <multi-locale-input
                            formControlName="last_names"
                            [inputLabel]="lastnameLabel"
                            [locales]="multiLocaleConfig.supportedLocales"
                            [displayLabelKey]="multiLocaleConfig.displayLabelKey"
                            [localeKey]="multiLocaleConfig.localeKey"
                            [defaultLocale]="multiLocaleConfig.defaultLocale"
                            [placeholderText]="lastnamePlaceholder"
                            [required]="true"
                            [id]="'user_last_name'"
                            data-automation="lastName"
                        >
                        </multi-locale-input>
                    </div>

                    <div class="form-entry">
                        <!-- Email -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.email' | translate }}</mat-label>
                            <input
                                matInput
                                id="email"
                                type="email"
                                class="input-entry"
                                data-automation="email"
                                placeholder="{{ 'user.create.placeholder.email' | translate }}"
                                maxlength="100"
                                formControlName="email"
                                data-url-persist
                            />
                            <mat-error
                                *ngIf="
                                    userForm.get('email').invalid &&
                                    (userForm.get('email').dirty || userForm.get('email').touched)
                                "
                            >
                                <mat-error *ngIf="userForm.get('email').errors?.required">
                                    <span data-automation="emailErrorRequired">{{
                                        'user.validation.required' | translate
                                    }}</span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('email').errors?.pattern">
                                    <span>{{ 'user.validation.pattern' | translate }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- Username -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.user_name' | translate }} </mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="usernameHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.username' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <input
                                matInput
                                id="username"
                                type="text"
                                class="input-entry"
                                maxlength="70"
                                placeholder="{{ 'user.create.placeholder.user_name' | translate }}"
                                formControlName="username"
                                data-url-persist
                                data-automation="username"
                                matTooltip="{{ 'user.tooltip.username' | translate }}"
                            />

                            <mat-error
                                *ngIf="
                                    userForm.get('username').invalid &&
                                    (userForm.get('username').dirty || userForm.get('username').touched)
                                "
                            >
                                <mat-error *ngIf="userForm.get('username').errors?.required">
                                    <span data-automation="usernameErrorRequired">{{
                                        'user.validation.required' | translate
                                    }}</span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('username').errors?.minlength">
                                    <span>{{ 'user.validation.minLength' | translate }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- Title -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.title' | translate }}</mat-label>
                            <input
                                matInput
                                id="title"
                                type="text"
                                class="input-entry"
                                placeholder="{{ 'user.create.placeholder.title' | translate }}"
                                maxlength="80"
                                formControlName="title"
                                data-automation="title"
                                data-url-persist
                            />
                        </mat-form-field>
                    </div>

                    <div class="form-entry" *ngIf="!isOnboardingResource">
                        <!-- User Type -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.user_type' | translate }}</mat-label>
                            <mat-select
                                id="userType"
                                class="input-entry create-select"
                                [class.loading-field]="loadingUserTypes"
                                formControlName="userType"
                                data-automation="userTypeSelector"
                                data-url-persist
                                placeholder="{{
                                    (userTypes && userTypes.length > 0
                                        ? 'user.create.placeholder.usertype_selector'
                                        : 'user.create.placeholder.no_usertypes'
                                    ) | translate
                                }}"
                            >
                                <mat-option *ngFor="let userType of filteredUserTypes" [value]="userType.name">
                                    {{ userType.display_label }}
                                </mat-option>
                            </mat-select>
                            <mat-error
                                *ngIf="
                                    userForm.get('userType').invalid &&
                                    (userForm.get('userType').dirty || userForm.get('userType').touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('userType').errors?.required"
                                    data-automation="userTypeErrorRequired"
                                >
                                    {{ 'user.validation.required' | translate }}
                                </mat-error>
                            </mat-error>
                            <mat-hint *ngIf="loadingUserTypes" align="end">{{ 'user.loading' | translate }}</mat-hint>
                            <mat-spinner *ngIf="loadingUserTypes" matSuffix diameter="23"></mat-spinner>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- Supported Locales -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.supported_locales' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="supportedLocalesHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.supported_locales' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="supportedLocale"
                                class="input-entry create-select"
                                [(ngModel)]="multiLocaleConfig.supportedLocales"
                                [ngModelOptions]="{ standalone: true }"
                                (selectionChange)="onSupportedLocaleChange()"
                                placeholder="{{ 'user.create.placeholder.supported_locales' | translate }}"
                                data-automation="locale"
                                required
                                multiple
                                matTooltip="{{ 'user.tooltip.supported_locales' | translate }}"
                            >
                                <mat-option
                                    *ngFor="let locale of locales"
                                    [disabled]="limitLocaleSelectList(5, locale)"
                                    [value]="locale"
                                    >{{ locale.displayLabel }}</mat-option
                                >
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- Locale -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.locale' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="localeHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.locale' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="locale"
                                class="input-entry create-select"
                                formControlName="locale"
                                data-automation="localeSelector"
                                placeholder="{{ 'user.create.placeholder.locale_selector' | translate }}"
                                matTooltip="{{ 'user.tooltip.user_preferences.locale' | translate }}"
                            >
                                <mat-option
                                    *ngFor="let locale of multiLocaleConfig.supportedLocales"
                                    [value]="locale.localeName"
                                >
                                    {{ locale.displayLabel }}
                                </mat-option>
                            </mat-select>
                            <mat-error *ngIf="userForm.get('locale').invalid">
                                <mat-error *ngIf="userForm.get('locale').errors.pattern">
                                    {{ 'user.validation.required' | translate }}
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry" *ngIf="!isOnboardingResource">
                        <!-- Brand -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.brand' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="brandHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.brand' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="brand"
                                class="input-entry create-select"
                                [class.loading-field]="loadingBrands"
                                formControlName="brand"
                                data-automation="brandSelector"
                                data-url-persist
                                matTooltip="{{ 'user.tooltip.brand' | translate }}"
                            >
                                <mat-option *ngFor="let brand of filteredBrands" [value]="brand.name">
                                    {{ brand.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!--- Country --->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.country' | translate }}</mat-label>
                            <mat-select
                                id="country"
                                class="input-entry create-select"
                                [formControl]="addressesFormArray.controls[0].get('country')"
                                data-automation="country"
                                (selectionChange)="onCountryChange($event)"
                                placeholder="{{ 'user.create.placeholder.country_selector_required' | translate }}"
                            >
                                <mat-option [value]="">{{
                                    'user.create.placeholder.country_selector_required' | translate
                                }}</mat-option>
                                <mat-option *ngFor="let country of countries" [value]="country.name">{{
                                    country.display_label
                                }}</mat-option>
                            </mat-select>
                            <mat-error
                                *ngIf="
                                    addressesFormArray.controls[0].get('country').invalid &&
                                    (addressesFormArray.controls[0].get('country').dirty ||
                                        addressesFormArray.controls[0].get('country').touched)
                                "
                            >
                                <mat-error *ngIf="addressesFormArray.controls[0].get('country').errors?.required">
                                    {{ 'user.validation.required' | translate }}
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry" *ngIf="!isOnboardingResource">
                        <span>
                            <mat-checkbox
                                [disabled]="userForm.get('userClass').disabled"
                                data-automation="system_level"
                                id="system_level"
                                [checked]="userForm.get('userClass').value === 'SYSTEM'"
                                (change)="onSystemLevelChange($event)"
                                matTooltip="{{ 'user.tooltip.system_user' | translate }}"
                            >
                                {{ 'user.system_user' | translate }}
                            </mat-checkbox>
                            <mat-icon
                                data-automation="systemUserHelpIcon"
                                class="tooltip-icon-checkbox"
                                matTooltip="{{ 'user.tooltip.system_user' | translate }}"
                                >info_outline</mat-icon
                            >
                        </span>
                    </div>

                    <ng-container *ngIf="!isOnboardingResource && isSystemUser()">
                        <div class="form-entry" aria-hidden="true"></div>
                        <div class="form-entry">
                            <span>
                                <mat-checkbox
                                    formControlName="asset_manager"
                                    data-automation="asset_manager"
                                    id="asset_manager"
                                    matTooltip="{{ 'user.tooltip.asset_manager' | translate }}"
                                >
                                    {{ 'user.asset_manager' | translate }}
                                </mat-checkbox>
                                <mat-icon
                                    data-automation="assetManagerHelpIcon"
                                    class="tooltip-icon-checkbox"
                                    matTooltip="{{ 'user.tooltip.asset_manager' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </span>
                        </div>
                    </ng-container>
                </div>
                
                <div class="form-group reduced-width" data-automation="api_keys_section">
                    <!-- API Keys subsection (system users only) -->
                    <ng-container *ngIf="isSystemUser()">
                        <div class="mb-1">
                            <h4 data-automation="api_keys">{{ 'user.api_keys' | translate }}</h4>
                        </div>

                        <div class="form-entry">
                            <!-- API Key ID -->
                            <mat-form-field appearance="outline" floatLabel="always" subscriptSizing="dynamic">
                                <mat-label>{{ 'user.api_key_id' | translate }}</mat-label>
                                <mat-label>
                                    <mat-icon
                                        data-automation="apiKeyIdHelpIcon"
                                        class="tooltip-icon"
                                        matTooltip="{{ 'user.tooltip.api_key_id' | translate }}"
                                        >info_outline</mat-icon
                                    >
                                </mat-label>
                                <input
                                    matInput
                                    id="api_key_id"
                                    type="text"
                                    class="input-entry"
                                    data-automation="api_key_id"
                                    placeholder="{{ 'user.create.placeholder.api_key_id' | translate }}"
                                    formControlName="api_key_id"
                                    [readonly]="!!apiKeyToken"
                                    data-url-persist
                                    matTooltip="{{ 'user.tooltip.api_key_id' | translate }}"
                                />
                                <mat-error
                                    *ngIf="
                                        !apiKeyToken &&
                                        userForm.get('api_key_id').invalid &&
                                        (userForm.get('api_key_id').dirty || userForm.get('api_key_id').touched)
                                    "
                                >
                                    <mat-error *ngIf="userForm.get('api_key_id').errors?.pattern">
                                        <span data-automation="apiKeyIdErrorPattern">{{
                                            'user.validation.api_key_id.pattern' | translate
                                        }}</span>
                                    </mat-error>
                                </mat-error>
                            </mat-form-field>
                        </div>

                        <div class="form-entry">
                            <!-- Display Label -->
                            <mat-form-field appearance="outline" floatLabel="always">
                                <mat-label>{{ 'user.key_display_label' | translate }}</mat-label>
                                <mat-label>
                                    <mat-icon
                                        data-automation="keyDisplayLabelHelpIcon"
                                        class="tooltip-icon"
                                        matTooltip="{{ 'user.tooltip.key_display_label' | translate }}"
                                        >info_outline</mat-icon
                                    >
                                </mat-label>
                                <input
                                    matInput
                                    id="key_display_label"
                                    type="text"
                                    class="input-entry"
                                    data-automation="key_display_label"
                                    placeholder="{{ 'user.create.placeholder.key_display_label' | translate }}"
                                    formControlName="key_display_label"
                                    [readonly]="!!apiKeyToken"
                                    data-url-persist
                                    matTooltip="{{ 'user.tooltip.key_display_label' | translate }}"
                                />

                            </mat-form-field>
                        </div>

                        <!-- Cross-field validation error for API key fields -->
                        <mat-error
                            *ngIf="apiKeysGroup.errors?.apiKeyFieldsMismatch"
                            data-automation="apiKeyFieldsMismatchError"
                        >
                            <span>{{ 'user.validation.api_key_fields_mismatch' | translate }}</span>
                        </mat-error>

                        <!-- API Key Token (visible once key is generated) -->
                        <div class="form-entry" *ngIf="apiKeyToken">
                            <mat-form-field appearance="outline" floatLabel="always">
                                <mat-label>{{ 'user.api_key_token' | translate }}</mat-label>
                                <input
                                    matInput
                                    id="api_key_token"
                                    type="text"
                                    class="input-entry"
                                    data-automation="api_key_token"
                                    [value]="apiKeyToken"
                                    readonly
                                />
                            </mat-form-field>
                        </div>

                        <!-- Manual retry button -->
                        <div class="form-entry" *ngIf="isUserCreated() && !apiKeyToken">
                            <button
                                data-automation="createApiKeyButton"
                                class="icon-btn x-btn-primary"
                                [disabled]="!userForm.get('api_key_id').valid || !userForm.get('key_display_label').valid || isSaving"
                                (click)="createApiKey()"
                            >
                                <mat-icon *ngIf="!isSaving">vpn_key</mat-icon>
                                <div *ngIf="isSaving" class="btn-spinner">
                                    <mat-spinner diameter="15"></mat-spinner>
                                </div>
                                {{ 'user.create_api_key' | translate }}
                            </button>
                        </div>
                    </ng-container>
                </div>

                <div class="stepper-buttons">
                    <button
                        data-automation="identificationUserSubmit"
                        class="icon-btn x-btn-primary"
                        [disabled]="!userForm.valid || !apiKeysGroup.valid || isSaving || !identificationHasChanged"
                        (click)="saveIdentification()"
                    >
                        <mat-icon *ngIf="!isSaving">check</mat-icon>
                        <div *ngIf="isSaving" class="btn-spinner">
                            <mat-spinner diameter="15"></mat-spinner>
                        </div>
                        <span class="btn-text">{{ 'user.button.save' | translate }}</span>
                    </button>
                    <button
                        data-automation="identificationUserCreateNext"
                        class="icon-btn x-btn-secondary"
                        matStepperNext
                        [disabled]="!isUserCreated()"
                    >
                        <mat-icon>chevron_right</mat-icon>
                        <span class="btn-text">{{ 'user.button.next' | translate }}</span>
                    </button>
                </div>
            </mat-step>

            <!-- Step 2: User Roles -->
            <mat-step
                label="{{ 'user.stepper.roles' | translate }}"
                [attr.step-disabled]="true"
                [stepControl]="userRolesGroup"
            >
                <div class="form-group">
                    <ng-container>
                        <h4 class="form-header">{{ 'user.edit.location_roles' | translate }}</h4>
                        <app-location-roles
                            [mode]="CREATE"
                            [userId]="userId"
                            [hasEcrm]="hasEcrm"
                            [isOnboarding]="isOnboardingResource"
                            (applyingRolesEvent)="applyRoles($event)"
                            (onRoleAdd)="onRoleAdd()"
                        >
                        </app-location-roles>
                    </ng-container>

                    <div class="form-header-container">
                        <h4 class="form-header-no-border">{{ 'user.edit.active_location_roles' | translate }}</h4>
                        <span>
                            <span>{{ 'user.edit.roles_added' | translate }} {{ addedRoles }}</span>
                            <span class="ml-1">{{ 'user.edit.roles_deleted' | translate }} {{ removedRoles }}</span>
                        </span>
                    </div>

                    <app-active-location-roles
                        [mode]="EDIT"
                        [showControls]="false"
                        [isOnboarding]="isOnboardingResource"
                        [changed]="userRolesHasChanged$ | async"
                        [hasEcrm]="ecrmId !== '' && ecrm_id !== null && ecrm_id !== undefined && ecrm_id.length == 18"
                        (applyingRolesEvent)="applyRoles($event)"
                        (onRoleRemoval)="onRoleRemoval($event)"
                        (unRemoveRoleEvent)="unRemoveRole($event)"
                        (onSiteRemoval)="onSiteRemoval($event)"
                        (onRoleUpdate)="newSubmitRoles()"
                    ></app-active-location-roles>
                </div>

                <div class="stepper-buttons">
                    <button
                        class="icon-btn x-btn-primary"
                        data-automation="rolesSubmit"
                        [disabled]="!userRolesGroup.valid || !(userRolesHasChanged$ | async)"
                        (click)="saveRoles()"
                    >
                        <mat-icon>check</mat-icon>
                        <span class="btn-text">{{ 'user.button.save' | translate }}</span>
                    </button>
                    <button
                        class="icon-btn x-btn-secondary"
                        data-automation="userRolesUserCreateNext"
                        matStepperNext
                        [disabled]="!userRolesGroup.valid"
                    >
                        <mat-icon>chevron_right</mat-icon>
                        <span class="btn-text">{{ 'user.button.next' | translate }}</span>
                    </button>
                    <button
                        data-automation="userRolesUserReset"
                        class="icon-btn x-btn-secondary"
                        (click)="resetRoles()"
                    >
                        <mat-icon>refresh</mat-icon>
                        <span class="btn-text">{{ 'user.button.reset' | translate }}</span>
                    </button>
                </div>
            </mat-step>

            <!-- Step 3: Contact Information -->
            <mat-step
                label="{{ 'user.stepper.contact' | translate }}"
                [disabled]="userSubmitService.submittingUser$ | async"
            >
                <div class="form-group">
                    <div class="form-entry column-3">
                        <!-- Country calling code -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.countryCode' | translate }}</mat-label>
                            <mat-select
                                id="countryCode"
                                class="input-entry create-select"
                                [formControl]="phonesFormArray.controls[0].get('country_code')"
                                data-automation="countryCode"
                                placeholder="{{
                                    (phoneFieldValidation
                                        ? 'user.create.placeholder.country_code_required'
                                        : 'user.create.placeholder.country_code'
                                    ) | translate
                                }}"
                            >
                                <mat-option value="">{{
                                    'user.create.placeholder.country_code' | translate
                                }}</mat-option>
                                <mat-option *ngFor="let country of countries" [value]="country.callingCodes[0]">
                                    +{{ country.callingCodes[0] }} {{ country.display_label }}
                                </mat-option>
                            </mat-select>
                            <mat-error
                                *ngIf="
                                    !hasAnyVoiceNotificationRole &&
                                    userForm.get('phones')?.controls[0]?.get('country_code')?.invalid &&
                                    (userForm.get('phones')?.controls[0]?.get('country_code')?.dirty ||
                                        userForm.get('phones')?.controls[0]?.get('country_code')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[0]?.get('country_code')?.errors?.required"
                                >
                                    {{ 'user.validation.required' | translate }}
                                </mat-error>
                            </mat-error>
                            <mat-error
                                *ngIf="
                                    hasAnyVoiceNotificationRole &&
                                    userForm.get('phones')?.controls[0]?.get('country_code')?.invalid &&
                                    (userForm.get('phones')?.controls[0]?.get('country_code')?.dirty ||
                                        userForm.get('phones')?.controls[0]?.get('country_code')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[0]?.get('country_code')?.errors?.required"
                                >
                                    <span>{{
                                        'user.validation.roleVerification' | translate : { subscriptionType: 'voice' }
                                    }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry column-3">
                        <!-- Phone Number -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.phone_number' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="phoneIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.phone_number' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <input
                                matInput
                                id="phoneNumber"
                                data-automation="phoneNumber"
                                type="text"
                                class="input-entry"
                                placeholder="{{
                                    (phoneFieldValidation
                                        ? 'user.create.placeholder.phone_number_required'
                                        : 'user.create.placeholder.phone_number'
                                    ) | translate
                                }}"
                                [formControl]="phonesFormArray.controls[0].get('phone_number')"
                                data-url-persist
                                matTooltip="{{ 'user.tooltip.phone_number' | translate }}"
                            />
                            <mat-error
                                *ngIf="
                                    !hasAnyVoiceNotificationRole &&
                                    userForm.get('phones')?.controls[0]?.get('phone_number')?.invalid &&
                                    (userForm.get('phones')?.controls[0]?.get('phone_number')?.dirty ||
                                        userForm.get('phones')?.controls[0]?.get('phone_number')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[0]?.get('phone_number')?.errors?.required"
                                >
                                    <span>{{ 'user.validation.required' | translate }}</span>
                                </mat-error>
                            </mat-error>
                            <mat-error
                                *ngIf="
                                    hasAnyVoiceNotificationRole &&
                                    userForm.get('phones')?.controls[0]?.get('phone_number')?.invalid &&
                                    (userForm.get('phones')?.controls[0]?.get('phone_number')?.dirty ||
                                        userForm.get('phones')?.controls[0]?.get('phone_number')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[0]?.get('phone_number')?.errors?.required"
                                >
                                    <span>{{
                                        'user.validation.roleVerification' | translate : { subscriptionType: 'voice' }
                                    }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry column-3">
                        <!-- Phone Extension -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.phone_extension' | translate }}</mat-label>
                            <input
                                matInput
                                id="phoneExtension"
                                type="text"
                                class="input-entry"
                                data-automation="phoneExtension"
                                placeholder="{{ 'user.create.placeholder.extension' | translate }}"
                                [formControl]="phonesFormArray.controls[0].get('extension')"
                                maxlength="9"
                                data-url-persist
                            />
                        </mat-form-field>
                    </div>

                    <div class="form-entry column-3">
                        <!-- Mobile Country Code -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.mobileCountryCode' | translate }}</mat-label>
                            <mat-select
                                id="mobileCountryCode"
                                class="input-entry create-select"
                                [formControl]="phonesFormArray.controls[1]?.get('country_code')"
                                data-automation="countryCode"
                                placeholder="{{
                                    (mobilePhoneFieldValidation
                                        ? 'user.create.placeholder.country_code_required'
                                        : 'user.create.placeholder.country_code'
                                    ) | translate
                                }}"
                            >
                                <mat-option value="">{{
                                    'user.create.placeholder.country_code' | translate
                                }}</mat-option>
                                <mat-option *ngFor="let country of countries" [value]="country.callingCodes[0]">
                                    +{{ country.callingCodes[0] }} {{ country.display_label }}
                                </mat-option>
                            </mat-select>
                            <mat-error
                                *ngIf="
                                    !hasAnySMSNotificationRole &&
                                    userForm.get('phones')?.controls[1]?.get('country_code')?.invalid &&
                                    (userForm.get('phones')?.controls[1]?.get('country_code')?.dirty ||
                                        userForm.get('phones')?.controls[1]?.get('country_code')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[1]?.get('country_code')?.errors?.required"
                                >
                                    {{ 'user.validation.required' | translate }}
                                </mat-error>
                            </mat-error>
                            <mat-error
                                *ngIf="
                                    hasAnySMSNotificationRole &&
                                    userForm.get('phones')?.controls[1]?.get('country_code')?.invalid &&
                                    (userForm.get('phones')?.controls[1]?.get('country_code')?.dirty ||
                                        userForm.get('phones')?.controls[1]?.get('country_code')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[1]?.get('country_code')?.errors?.required"
                                >
                                    <span>{{
                                        'user.validation.roleVerification' | translate : { subscriptionType: 'text' }
                                    }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry column-3">
                        <!-- Mobile Phone Number -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.mobile_phone_number' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="mobilePhoneIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.mobile_phone_number' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <input
                                matInput
                                id="mobilePhoneNumber"
                                data-automation="mobilePhoneNumber"
                                type="text"
                                class="input-entry"
                                placeholder="{{
                                    (mobilePhoneFieldValidation
                                        ? 'user.create.placeholder.mobile_phone_number_required'
                                        : 'user.create.placeholder.mobile_phone_number'
                                    ) | translate
                                }}"
                                [formControl]="phonesFormArray.controls[1]?.get('phone_number')"
                                data-url-persist
                                matTooltip="{{ 'user.tooltip.mobile_phone_number' | translate }}"
                            />
                            <mat-error
                                *ngIf="
                                    !hasAnySMSNotificationRole &&
                                    userForm.get('phones')?.controls[1]?.get('phone_number')?.invalid &&
                                    (userForm.get('phones')?.controls[1]?.get('phone_number')?.dirty ||
                                        userForm.get('phones')?.controls[1]?.get('phone_number')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[1]?.get('phone_number')?.errors?.required"
                                >
                                    <span>{{ 'user.validation.required' | translate }}</span>
                                </mat-error>
                            </mat-error>
                            <mat-error
                                *ngIf="
                                    hasAnySMSNotificationRole &&
                                    userForm.get('phones')?.controls[1]?.get('phone_number')?.invalid &&
                                    (userForm.get('phones')?.controls[1]?.get('phone_number')?.dirty ||
                                        userForm.get('phones')?.controls[1]?.get('phone_number')?.touched)
                                "
                            >
                                <mat-error
                                    *ngIf="userForm.get('phones')?.controls[1]?.get('phone_number')?.errors?.required"
                                >
                                    <span>{{
                                        'user.validation.roleVerification' | translate : { subscriptionType: 'text' }
                                    }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry-column-checks">
                        <!-- Primary Contact -->
                        <div class="form-entry">
                            <span>
                                <mat-checkbox
                                    data-automation="primary_contact"
                                    id="primary_contact"
                                    formControlName="primary_contact"
                                    matTooltip="{{ 'user.tooltip.primary_contact' | translate }}"
                                >
                                    {{ 'user.primary_contact' | translate }}
                                </mat-checkbox>
                                <mat-icon
                                    data-automation="primaryContactHelpIcon"
                                    class="tooltip-icon-checkbox"
                                    matTooltip="{{ 'user.tooltip.primary_contact' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </span>
                        </div>

                        <!-- 24/7 Contact -->
                        <div class="form-entry">
                            <span>
                                <mat-checkbox
                                    data-automation="twentyfour_seven_contact"
                                    id="twentyfour_seven_contact"
                                    formControlName="twentyfour_seven_contact"
                                    matTooltip="{{ 'user.tooltip.24_7_contact' | translate }}"
                                >
                                    {{ 'user.24_7_contact' | translate }}
                                </mat-checkbox>
                                <mat-icon
                                    data-automation="twentyFourSevenContactHelpIcon"
                                    class="tooltip-icon-checkbox"
                                    matTooltip="{{ 'user.tooltip.24_7_contact' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </span>
                        </div>
                    </div>
                    <div class="form-group table-container">
                        <app-subscription-table
                            mode="edit"
                            [userId]="userId"
                            [hasEmailField]="email !== '' && email !== null && email !== undefined"
                            [hasAnyPhoneField]="hasAnyPhoneField"
                            [hasAnyMobilePhoneField]="hasAnyMobilePhoneField"
                            [hasContactMethodPush]="hasContactMethodPush"
                            [hasOnboardingRole]="hasOnboardingRole"
                            [hasDRFinancialRole]="hasDRFinancialRole"
                            (addSubscriptionEvent)="addSubscription($event)"
                            (delSubscriptionEvent)="removeSubscription($event)"
                        ></app-subscription-table>
                    </div>
                    <div class="form-entry-column-checks">
                        <!-- User Activation -->
                        <div class="form-entry">
                            <span>
                                <mat-checkbox
                                    data-automation="send_activation"
                                    id="send_activation"
                                    formControlName="send_activation"
                                    matTooltip="{{ 'user.tooltip.send_activation' | translate }}"
                                >
                                    {{ 'user.create.activate_on_submit' | translate }}
                                </mat-checkbox>
                                <mat-icon
                                    data-automation="sendActivationHelpIcon"
                                    class="tooltip-icon-checkbox"
                                    matTooltip="{{ 'user.tooltip.send_activation' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </span>
                        </div>
                    </div>
                </div>

                <div class="stepper-buttons">
                    <button
                        data-automation="contactInformationUserSubmit"
                        class="icon-btn x-btn-primary"
                        [disabled]="userForm.get('phones').invalid || contactInformationStepHasChanged()"
                        (click)="saveContactInformation()"
                    >
                        <mat-icon>check</mat-icon>
                        <span class="btn-text">{{ 'user.button.save' | translate }}</span>
                    </button>
                    <button
                        class="icon-btn x-btn-secondary"
                        data-automation="contactInformationUserCreateNext"
                        matStepperNext
                    >
                        <mat-icon>chevron_right</mat-icon>
                        <span class="btn-text">{{ 'user.button.next' | translate }}</span>
                    </button>
                </div>
            </mat-step>

            <!-- Step 4: Sync Information -->
            <mat-step
                label="{{ 'user.stepper.sync' | translate }}"
                [disabled]="userSubmitService.submittingUser$ | async"
                *ngIf="showSyncSection"
            >
                <div class="form-group reduced-width" data-automation="syncInfoSectionCreate">
                    <div class="form-entry">
                        <!-- ECRM Contact ID -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.ecrm_contact_id' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="ecrmContactHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.ecrm_contact_id' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <input
                                matInput
                                id="contact_id"
                                type="text"
                                class="input-entry"
                                placeholder="{{ 'user.ecrm_contact_id' | translate }}"
                                formControlName="contact_id"
                                data-url-persist
                                data-automation="contact_id"
                                (input)="onECRMContactIDChange()"
                                matTooltip="{{ 'user.tooltip.ecrm_contact_id' | translate }}"
                            />
                            <mat-error
                                *ngIf="
                                    userForm.get('contact_id').invalid &&
                                    (userForm.get('contact_id').dirty || userForm.get('contact_id').touched)
                                "
                            >
                                <mat-error *ngIf="userForm.get('contact_id').errors?.pattern">
                                    <span data-automation="ecrmContactIDLengthError">
                                        {{ 'user.validation.ecrm.contact_id_length' | translate }}
                                    </span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('contact_id').errors?.start">
                                    <span data-automation="ecrmContactIDStartError">
                                        {{ 'user.validation.ecrm.contact_id_start' | translate }}
                                    </span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- ECRM User ID -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.ecrm_id' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="ecrmUserHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.ecrm_user_id' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <input
                                matInput
                                id="ecrmID"
                                type="text"
                                class="input-entry"
                                placeholder="{{ 'user.ecrm_id' | translate }}"
                                formControlName="ecrm_id"
                                data-url-persist
                                data-automation="ecrmID"
                                (input)="onECRMUserIDChange()"
                                matTooltip="{{ 'user.tooltip.ecrm_user_id' | translate }}"
                            />
                            <mat-error
                                *ngIf="
                                    userForm.get('ecrm_id').invalid &&
                                    (userForm.get('ecrm_id').dirty || userForm.get('ecrm_id').touched)
                                "
                            >
                                <mat-error *ngIf="userForm.get('ecrm_id').errors?.required">
                                    <span>{{ 'user.validation.required' | translate }}</span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('ecrm_id').errors?.pattern">
                                    <span data-automation="ecrmIDLengthError">
                                        {{ 'user.validation.ecrm.length' | translate }}
                                    </span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('ecrm_id').errors?.start">
                                    <span data-automation="ecrmIDStartError">
                                        {{ 'user.validation.ecrm.start' | translate }}
                                    </span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>

                    <div class="form-entry" *ngIf="!isOnboardingResource">
                        <div class="checkbox-feedback">
                            <span>
                                <mat-checkbox
                                    data-automation="ecrmSync"
                                    id="sync_to_ecrm"
                                    formControlName="sync_to_ecrm"
                                    (change)="handleEcrmChange()"
                                    matTooltip="{{ 'user.tooltip.sync_to_ecrm' | translate }}"
                                >
                                    {{ 'user.sync_to_ecrm' | translate }}
                                </mat-checkbox>
                                <mat-icon
                                    data-automation="syncToEcrmHelpIcon"
                                    class="tooltip-icon-checkbox"
                                    matTooltip="{{ 'user.tooltip.sync_to_ecrm' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </span>

                            <span *ngIf="userForm.get('last_sync_date').value" class="last-synced">
                                {{ 'user.last_sync_date' | translate }}
                                {{ userForm.get('last_sync_date').value | date : 'medium' }}
                            </span>
                        </div>
                    </div>

                    <div class="form-entry">
                        <!--- Country --->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.country' | translate }}</mat-label>
                            <mat-select
                                id="country_1"
                                (input)="onAddressFieldChange($event)"
                                class="input-entry create-select"
                                [formControl]="addressesFormArray.controls[0].get('country')"
                                data-automation="country_1"
                                (selectionChange)="onCountryChange($event)"
                                placeholder="{{ 'user.create.placeholder.country_selector' | translate }}"
                            >
                                <mat-option>{{ 'user.create.placeholder.country_selector' | translate }}</mat-option>
                                <mat-option *ngFor="let country of countries" [value]="country.name">{{
                                    country.display_label
                                }}</mat-option>
                            </mat-select>
                            <mat-error
                                *ngIf="
                                    addressesFormArray.controls[0].get('country').invalid &&
                                    (addressesFormArray.controls[0].get('country').dirty ||
                                        addressesFormArray.controls[0].get('country').touched)
                                "
                            >
                                <mat-error *ngIf="addressesFormArray.controls[0].get('country').errors?.required">
                                    {{ 'user.validation.required' | translate }}
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>
                </div>

                <div class="stepper-buttons">
                    <button
                        class="icon-btn x-btn-primary"
                        (click)="saveSyncInformation()"
                        data-automation="syncInformationUserSubmit"
                        [disabled]="!this.syncInformationHasChanged"
                    >
                        <mat-icon>check</mat-icon>
                        <span class="btn-text">{{ 'user.button.save' | translate }}</span>
                    </button>
                    <button
                        class="icon-btn x-btn-secondary"
                        data-automation="syncInformationUserCreateNext"
                        matStepperNext
                    >
                        <mat-icon>chevron_right</mat-icon>
                        <span class="btn-text">{{ 'user.button.next' | translate }}</span>
                    </button>
                </div>
            </mat-step>

            <!-- Step 5: User Settings -->
            <mat-step
                label="{{ 'user.stepper.settings' | translate }}"
                [disabled]="userSubmitService.submittingUser$ | async"
            >
                <div class="form-group reduced-width">
                    <div class="form-entry">
                        <!-- Platform Locale selector -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.platform_locale' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="userPreferencesLocaleHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.user_preferences.locale' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="locale"
                                class="input-entry create-select"
                                formControlName="locale"
                                data-automation="localeSelector"
                            >
                                <mat-option
                                    *ngFor="let locale of multiLocaleConfig.supportedLocales"
                                    [value]="locale.localeName"
                                >
                                    {{ locale.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- Timezone selector-->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.timezone' | translate }}</mat-label>
                            <mat-select
                                id="timezone"
                                class="input-entry create-select"
                                formControlName="timezone"
                                data-automation="timezoneSelector"
                            >
                                <mat-option *ngFor="let timezone of timezones" [value]="timezone.timezoneName">
                                    {{ timezone.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <div class="form-entry">
                        <!-- System of measurement selector-->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.measurement' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="userPreferencesMeasurementHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.measurement' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="measurement"
                                class="input-entry create-select"
                                formControlName="unit_system"
                                data-automation="measurement_systemSelector"
                                placeholder="{{ 'user.create.placeholder.measurement_selector' | translate }}"
                                matTooltip="{{ 'user.tooltip.measurement' | translate }}"
                            >
                                <mat-option *ngFor="let measurement of measurements" [value]="measurement.name">
                                    {{ measurement.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                    <div class="form-entry">
                        <!-- Home Application selector-->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.application' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="userPreferencesHomeAppHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.home_application' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="application"
                                class="input-entry create-select"
                                formControlName="home_application"
                                data-automation="home_applicationSelector"
                                placeholder="{{ 'user.create.placeholder.application_selector' | translate }}"
                                matTooltip="{{ 'user.tooltip.home_application' | translate }}"
                            >
                                <mat-option *ngFor="let application of applications" [value]="application.name">
                                    {{ application.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>

                <div class="stepper-buttons">
                    <button
                        class="icon-btn x-btn-primary"
                        data-automation="userSubmit"
                        (click)="saveUserSettings()"
                        [disabled]="!this.userSettingsHasChanged"
                    >
                        <mat-icon>check</mat-icon>
                        <span class="btn-text">{{ 'user.button.save' | translate }}</span>
                    </button>
                </div>
            </mat-step>
        </mat-stepper>
    </div>

    <!--- EDIT --->
    <div *ngIf="isEditMode && !isRetired">
        <!--- SECTION 1 --->
        <div>
            <div class="form-group">
                <div class="form-entry" *ngIf="isInternal">
                    <!-- Enel Employee ID -->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.enel_employee_id' | translate }}</mat-label>
                        <input
                            matInput
                            id="enel_employee_id"
                            type="text"
                            class="input-entry"
                            data-automation="enel_employee_id"
                            placeholder="{{ 'user.create.placeholder.enel_employee_id' | translate }}"
                            maxlength="100"
                            formControlName="enel_employee_id"
                            data-url-persist
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('enel_employee_id').invalid &&
                                (userForm.get('enel_employee_id').dirty || userForm.get('enel_employee_id').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('enel_employee_id').errors?.required">
                                <span data-automation="enelEmployeeIdErrorRequired">{{
                                    'user.validation.required' | translate
                                }}</span>
                            </mat-error>
                            <mat-error *ngIf="userForm.get('enel_employee_id').errors?.pattern">
                                <span>{{ 'user.validation.pattern' | translate }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>
                <div class="form-entry">
                    <!--- First Name --->
                    <multi-locale-input
                        formControlName="first_names"
                        [inputLabel]="firstnameLabel"
                        [locales]="multiLocaleConfig.supportedLocales"
                        [displayLabelKey]="multiLocaleConfig.displayLabelKey"
                        [localeKey]="multiLocaleConfig.localeKey"
                        [defaultLocale]="multiLocaleConfig.defaultLocale"
                        [placeholderText]="firstnamePlaceholder"
                        [required]="true"
                        [id]="'user_first_name'"
                        data-automation="firstName"
                    >
                    </multi-locale-input>
                </div>

                <div class="form-entry">
                    <!--- Last Name --->
                    <multi-locale-input
                        formControlName="last_names"
                        [inputLabel]="lastnameLabel"
                        [locales]="multiLocaleConfig.supportedLocales"
                        [displayLabelKey]="multiLocaleConfig.displayLabelKey"
                        [localeKey]="multiLocaleConfig.localeKey"
                        [defaultLocale]="multiLocaleConfig.defaultLocale"
                        [placeholderText]="lastnamePlaceholder"
                        [required]="true"
                        [id]="'user_last_name'"
                        data-automation="lastName"
                    >
                    </multi-locale-input>
                </div>

                <div class="form-entry">
                    <!--- Username --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.user_name' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="usernameHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.username' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="username"
                            type="text"
                            class="input-entry"
                            maxlength="70"
                            placeholder="{{ 'user.create.placeholder.user_name' | translate }}"
                            formControlName="username"
                            data-url-persist
                            data-automation="username"
                            matTooltip="{{ 'user.tooltip.username' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('username').invalid &&
                                (userForm.get('username').dirty || userForm.get('username').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('username').errors?.required">
                                <span>{{ 'user.validation.required' | translate }}</span>
                            </mat-error>
                            <mat-error *ngIf="userForm.get('username').errors?.minlength">
                                <span>{{ 'user.validation.minLength' | translate }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry" *ngIf="!isOnboardingResource">
                    <!--- User Type --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.user_type' | translate }}</mat-label>
                        <mat-select
                            id="userType"
                            class="input-entry create-select"
                            [class.loading-field]="loadingUserTypes"
                            formControlName="userType"
                            data-automation="userTypeSelector"
                            data-url-persist
                            placeholder="{{
                                (userTypes && userTypes.length > 0
                                    ? 'user.create.placeholder.usertype_selector'
                                    : 'user.create.placeholder.no_usertypes'
                                ) | translate
                            }}"
                        >
                            <mat-option *ngFor="let userType of filteredUserTypes" [value]="userType.name">
                                {{ userType.display_label }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                userForm.get('userType').invalid &&
                                (userForm.get('userType').dirty || userForm.get('userType').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('userType').errors?.required">
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                        <mat-hint *ngIf="loadingUserTypes" align="end">{{ 'user.loading' | translate }}</mat-hint>
                        <mat-spinner *ngIf="loadingUserTypes" matSuffix diameter="23"></mat-spinner>
                    </mat-form-field>
                </div>

                <div class="form-entry">
                    <!--- User Title --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.title' | translate }}</mat-label>
                        <input
                            matInput
                            id="title"
                            type="text"
                            class="input-entry"
                            placeholder="{{ 'user.create.placeholder.title' | translate }}"
                            maxlength="80"
                            formControlName="title"
                            data-automation="title"
                            data-url-persist
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('title').invalid &&
                                (userForm.get('title').dirty || userForm.get('title').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('title').errors?.required">
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry">
                    <!--- supported locales --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.supported_locales' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="supportedLocalesHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.supported_locales' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <mat-select
                            id="supportedLocale"
                            class="input-entry create-select"
                            [(ngModel)]="multiLocaleConfig.supportedLocales"
                            [ngModelOptions]="{ standalone: true }"
                            (selectionChange)="onSupportedLocaleChange()"
                            placeholder="{{ 'user.create.placeholder.supported_locales' | translate }}"
                            data-automation="locale"
                            required
                            multiple
                            matTooltip="{{ 'user.tooltip.supported_locales' | translate }}"
                        >
                            <mat-option
                                *ngFor="let locale of locales"
                                [disabled]="limitLocaleSelectList(5, locale)"
                                [value]="locale"
                                >{{ locale.displayLabel }}</mat-option
                            >
                        </mat-select>
                    </mat-form-field>
                </div>

                <div class="form-entry">
                    <!--- default locale --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.locale' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="localeHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.locale' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <mat-select
                            id="locale"
                            class="input-entry create-select"
                            formControlName="locale"
                            data-automation="localeSelector"
                            placeholder="{{ 'user.create.placeholder.locale_selector' | translate }}"
                            matTooltip="{{ 'user.tooltip.user_preferences.locale' | translate }}"
                        >
                            <mat-option
                                *ngFor="let locale of multiLocaleConfig.supportedLocales"
                                [value]="locale.localeName"
                            >
                                {{ locale.displayLabel }}
                            </mat-option>
                        </mat-select>
                        <mat-error *ngIf="userForm.get('locale').invalid">
                            <mat-error *ngIf="userForm.get('locale').errors.pattern">
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry" *ngIf="!isOnboardingResource">
                    <!--- Brand --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.brand' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="brandHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.brand' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <mat-select
                            id="brand"
                            class="input-entry create-select"
                            [class.loading-field]="loadingBrands"
                            formControlName="brand"
                            data-automation="brandSelector"
                            data-url-persist
                            matTooltip="{{ 'user.tooltip.brand' | translate }}"
                        >
                            <mat-option *ngFor="let brandOption of filteredBrands" [value]="brandOption.name">
                                {{ brandOption.displayLabel }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>

                <div class="form-entry">
                    <!--- Country --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.country' | translate }}</mat-label>
                        <mat-select
                            id="country"
                            class="input-entry create-select"
                            [formControl]="addressesFormArray.controls[0].get('country')"
                            data-automation="country"
                            (selectionChange)="onCountryChange($event)"
                            placeholder="{{ 'user.create.placeholder.country_selector_required' | translate }}"
                        >
                            <mat-option [value]="">{{
                                'user.create.placeholder.country_selector_required' | translate
                            }}</mat-option>
                            <mat-option *ngFor="let country of countries" [value]="country.name">{{
                                country.display_label
                            }}</mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                addressesFormArray.controls[0].get('country').invalid &&
                                (addressesFormArray.controls[0].get('country').dirty ||
                                    addressesFormArray.controls[0].get('country').touched)
                            "
                        >
                            <mat-error *ngIf="addressesFormArray.controls[0].get('country').errors?.required">
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry" *ngIf="!isOnboardingResource && !isExternalOwnProfile">
                    <span>
                        <mat-checkbox
                            data-automation="system_level"
                            id="system_level"
                            [disabled]="userForm.get('userClass').disabled"
                            [checked]="userForm.get('userClass').value === 'SYSTEM'"
                            (change)="onSystemLevelChange($event)"
                            matTooltip="{{ 'user.tooltip.system_user' | translate }}"
                        >
                            {{ 'user.system_user' | translate }}
                        </mat-checkbox>
                        <mat-icon
                            data-automation="systemUserHelpIcon"
                            class="tooltip-icon-checkbox"
                            matTooltip="{{ 'user.tooltip.system_user' | translate }}"
                            >info_outline</mat-icon
                        >
                    </span>
                </div>

                <div class="form-entry" *ngIf="!isOnboardingResource">
                    <!--- User Contact ID --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.contact_id' | translate }}</mat-label>
                        <input
                            matInput
                            id="flex_contact_id"
                            type="text"
                            class="input-entry"
                            formControlName="flex_contact_id"
                            data-automation="flex_contact_id"
                            data-url-persist
                            [matTooltip]="userForm.get('flex_contact_id').value"
                        />
                    </mat-form-field>
                </div>

                <div class="form-entry" *ngIf="!isOnboardingResource && isSystemUser()">
                    <span>
                        <mat-checkbox
                            formControlName="asset_manager"
                            data-automation="asset_manager"
                            id="asset_manager"
                            matTooltip="{{ 'user.tooltip.asset_manager' | translate }}"
                        >
                            {{ 'user.asset_manager' | translate }}
                        </mat-checkbox>
                        <mat-icon
                            data-automation="assetManagerHelpIcon"
                            class="tooltip-icon-checkbox"
                            matTooltip="{{ 'user.tooltip.asset_manager' | translate }}"
                            >info_outline</mat-icon
                        >
                    </span>
                </div>
            </div>

            <div class="button-container">
                <div class="right-button">
                    <button class="icon-btn x-btn-secondary squared" (click)="collapseAll()">
                        <mat-icon>expand_less</mat-icon>
                        <span class="btn-text">{{ 'user.collapse_all' | translate }}</span>
                    </button>
                </div>
                <div class="right-button">
                    <button class="icon-btn x-btn-secondary squared" (click)="expandAll()">
                        <mat-icon>expand_more</mat-icon>
                        <span class="btn-text">{{ 'user.expand_all' | translate }}</span>
                    </button>
                </div>
            </div>
        </div>

        <mat-expansion-panel
            togglePosition="before"
            data-automation="contact_information_panel"
            [expanded]="expandableSectionsState[0]"
            (opened)="expansionOpened(0)"
            (closed)="expansionClosed(0)"
        >
            <mat-expansion-panel-header
                class="panel-override"
                collapsedHeight="54px"
                expandedHeight="54px"
                data-automation="contact_information_header"
            >
                <mat-panel-title>
                    <h4 class="card-title" data-automation="contact_information">
                        {{ 'user.contact_information' | translate }}
                    </h4>
                </mat-panel-title>
                <mat-panel-description class="contact_information_subtitle"> </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="form-group">
                <div>
                    <div class="form-entry column-3">
                        <!--- Email --->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.email' | translate }}</mat-label>
                            <input
                                matInput
                                id="email"
                                type="email"
                                class="input-entry"
                                data-automation="email"
                                placeholder="{{ 'user.create.placeholder.email' | translate }}"
                                maxlength="100"
                                formControlName="email"
                                data-url-persist
                            />
                            <mat-error
                                *ngIf="
                                    userForm.get('email').invalid &&
                                    (userForm.get('email').dirty || userForm.get('email').touched)
                                "
                            >
                                <mat-error *ngIf="userForm.get('email').errors?.required">
                                    <span>{{ 'user.validation.required' | translate }}</span>
                                </mat-error>
                                <mat-error *ngIf="userForm.get('email').errors?.pattern">
                                    <span>{{ 'user.validation.pattern' | translate }}</span>
                                </mat-error>
                            </mat-error>
                        </mat-form-field>
                    </div>
                </div>

                <!-- Country calling code -->
                <div class="form-entry column-3">
                    <!--- Country Calling Codes --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.countryCode' | translate }}</mat-label>
                        <mat-select
                            id="countryCode"
                            class="input-entry create-select"
                            [formControl]="phonesFormArray.controls[0].get('country_code')"
                            data-automation="countryCode"
                            placeholder="{{
                                (phoneFieldValidation
                                    ? 'user.create.placeholder.country_code_required'
                                    : 'user.create.placeholder.country_code'
                                ) | translate
                            }}"
                        >
                            <mat-option>{{ 'user.create.placeholder.country_code' | translate }}</mat-option>
                            <mat-option *ngFor="let country of countries" [value]="country.callingCodes[0]">
                                +{{ country.callingCodes[0] }} {{ country.display_label }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                !hasAnyVoiceNotificationRole &&
                                userForm.get('phones')?.controls[0]?.get('country_code')?.invalid &&
                                (userForm.get('phones')?.controls[0]?.get('country_code')?.dirty ||
                                    userForm.get('phones')?.controls[0]?.get('country_code')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[0]?.get('country_code')?.errors?.required"
                            >
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                        <mat-error
                            *ngIf="
                                hasAnyVoiceNotificationRole &&
                                userForm.get('phones')?.controls[0]?.get('country_code')?.invalid &&
                                (userForm.get('phones')?.controls[0]?.get('country_code')?.dirty ||
                                    userForm.get('phones')?.controls[0]?.get('country_code')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[0]?.get('country_code')?.errors?.required"
                            >
                                <span>{{
                                    'user.validation.roleVerification' | translate : { subscriptionType: 'voice' }
                                }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry column-3 label-info">
                    <!--- Phone Number --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.phone_number' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="phoneIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.phone_number' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="phoneNumber"
                            data-automation="phoneNumber"
                            type="text"
                            class="input-entry"
                            placeholder="{{
                                (phoneFieldValidation
                                    ? 'user.create.placeholder.phone_number_required'
                                    : 'user.create.placeholder.phone_number'
                                ) | translate
                            }}"
                            [formControl]="phonesFormArray.controls[0].get('phone_number')"
                            data-url-persist
                            matTooltip="{{ 'user.tooltip.phone_number' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                !hasAnyVoiceNotificationRole &&
                                userForm.get('phones')?.controls[0]?.get('phone_number')?.invalid &&
                                (userForm.get('phones')?.controls[0]?.get('phone_number')?.dirty ||
                                    userForm.get('phones')?.controls[0]?.get('phone_number')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[0]?.get('phone_number')?.errors?.required"
                            >
                                <span>{{ 'user.validation.required' | translate }}</span>
                            </mat-error>
                        </mat-error>
                        <mat-error
                            *ngIf="
                                hasAnyVoiceNotificationRole &&
                                userForm.get('phones')?.controls[0]?.get('phone_number')?.invalid &&
                                (userForm.get('phones')?.controls[0]?.get('phone_number')?.dirty ||
                                    userForm.get('phones')?.controls[0]?.get('phone_number')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[0]?.get('phone_number')?.errors?.required"
                            >
                                <span>{{
                                    'user.validation.roleVerification' | translate : { subscriptionType: 'voice' }
                                }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry column-3">
                    <!--- Phone Extension --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.phone_extension' | translate }}</mat-label>
                        <input
                            matInput
                            id="phoneExtension"
                            type="text"
                            class="input-entry"
                            data-automation="phoneExtension"
                            placeholder="{{ 'user.create.placeholder.extension' | translate }}"
                            [formControl]="phonesFormArray.controls[0].get('extension')"
                            maxlength="9"
                            data-url-persist
                        />
                    </mat-form-field>
                </div>

                <!--  Mobile Country calling code -->
                <div class="form-entry column-3">
                    <!--- Country Calling Codes --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.mobileCountryCode' | translate }}</mat-label>
                        <mat-select
                            id="mobileCountryCode"
                            class="input-entry create-select"
                            [formControl]="phonesFormArray.controls[1]?.get('country_code')"
                            data-automation="countryCode"
                            placeholder="{{
                                (mobilePhoneFieldValidation
                                    ? 'user.create.placeholder.country_code_required'
                                    : 'user.create.placeholder.country_code'
                                ) | translate
                            }}"
                        >
                            <mat-option>{{ 'user.create.placeholder.country_code' | translate }}</mat-option>
                            <mat-option *ngFor="let country of countries" [value]="country.callingCodes[0]">
                                +{{ country.callingCodes[0] }} {{ country.display_label }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                !hasAnySMSNotificationRole &&
                                userForm.get('phones')?.controls[1]?.get('country_code')?.invalid &&
                                (userForm.get('phones')?.controls[1]?.get('country_code')?.dirty ||
                                    userForm.get('phones')?.controls[1]?.get('country_code')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[1]?.get('country_code')?.errors?.required"
                            >
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                        <mat-error
                            *ngIf="
                                hasAnySMSNotificationRole &&
                                userForm.get('phones')?.controls[1]?.get('country_code')?.invalid &&
                                (userForm.get('phones')?.controls[1]?.get('country_code')?.dirty ||
                                    userForm.get('phones')?.controls[1]?.get('country_code')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[1]?.get('country_code')?.errors?.required"
                            >
                                <span>{{
                                    'user.validation.roleVerification' | translate : { subscriptionType: 'text' }
                                }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry column-3 label-info">
                    <!--- Mobile Phone Number --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.mobile_phone_number' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="mobilePhoneIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.mobile_phone_number' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="mobilePhoneNumber"
                            data-automation="mobilePhoneNumber"
                            type="text"
                            class="input-entry"
                            placeholder="{{
                                (mobilePhoneFieldValidation
                                    ? 'user.create.placeholder.mobile_phone_number_required'
                                    : 'user.create.placeholder.mobile_phone_number'
                                ) | translate
                            }}"
                            [formControl]="phonesFormArray.controls[1]?.get('phone_number')"
                            data-url-persist
                            matTooltip="{{ 'user.tooltip.mobile_phone_number' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                !hasAnySMSNotificationRole &&
                                userForm.get('phones')?.controls[1]?.get('phone_number')?.invalid &&
                                (userForm.get('phones')?.controls[1]?.get('phone_number')?.dirty ||
                                    userForm.get('phones')?.controls[1]?.get('phone_number')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[1]?.get('phone_number')?.errors?.required"
                            >
                                <span>{{ 'user.validation.required' | translate }}</span>
                            </mat-error>
                        </mat-error>
                        <mat-error
                            *ngIf="
                                hasAnySMSNotificationRole &&
                                userForm.get('phones')?.controls[1]?.get('phone_number')?.invalid &&
                                (userForm.get('phones')?.controls[1]?.get('phone_number')?.dirty ||
                                    userForm.get('phones')?.controls[1]?.get('phone_number')?.touched)
                            "
                        >
                            <mat-error
                                *ngIf="userForm.get('phones')?.controls[1]?.get('phone_number')?.errors?.required"
                            >
                                <span>{{
                                    'user.validation.roleVerification' | translate : { subscriptionType: 'text' }
                                }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>
                <div class="form-entry-column-checks">
                    <!--- primary_contact --->
                    <div class="form-entry">
                        <span>
                            <mat-checkbox
                                data-automation="primary_contact"
                                id="primary_contact"
                                formControlName="primary_contact"
                                matTooltip="{{ 'user.tooltip.primary_contact' | translate }}"
                            >
                                {{ 'user.primary_contact' | translate }}
                            </mat-checkbox>
                            <mat-icon
                                data-automation="primaryContactHelpIcon"
                                class="tooltip-icon-checkbox"
                                matTooltip="{{ 'user.tooltip.primary_contact' | translate }}"
                                >info_outline</mat-icon
                            >
                        </span>
                    </div>

                    <!--- twentyfour_seven_contact --->
                    <div class="form-entry">
                        <span>
                            <mat-checkbox
                                data-automation="twentyfour_seven_contact"
                                id="twentyfour_seven_contact"
                                formControlName="twentyfour_seven_contact"
                                matTooltip="{{ 'user.tooltip.24_7_contact' | translate }}"
                            >
                                {{ 'user.24_7_contact' | translate }}
                            </mat-checkbox>
                            <mat-icon
                                data-automation="twentyFourSevenContactHelpIcon"
                                class="tooltip-icon-checkbox"
                                matTooltip="{{ 'user.tooltip.24_7_contact' | translate }}"
                                >info_outline</mat-icon
                            >
                        </span>
                    </div>
                </div>
            </div>

            <div class="form-group table-container" *ngIf="isEditMode">
                <app-subscription-table
                    mode="edit"
                    [userId]="userId"
                    [hasEmailField]="email !== '' && email !== null && email !== undefined"
                    [hasAnyPhoneField]="hasAnyPhoneField"
                    [hasAnyMobilePhoneField]="hasAnyMobilePhoneField"
                    [hasContactMethodPush]="hasContactMethodPush"
                    [hasOnboardingRole]="hasOnboardingRole"
                    [hasDRFinancialRole]="hasDRFinancialRole"
                    (addSubscriptionEvent)="addSubscription($event)"
                    (delSubscriptionEvent)="removeSubscription($event)"
                ></app-subscription-table>
            </div>
        </mat-expansion-panel>

        <mat-expansion-panel
            togglePosition="before"
            data-automation="user_roles_panel"
            *ngIf="isEditMode && isNotOwnProfile()"
            [expanded]="expandableSectionsState[1]"
            (opened)="expansionOpened(1)"
            (closed)="expansionClosed(1)"
        >
            <mat-expansion-panel-header
                class="panel-override"
                collapsedHeight="54px"
                expandedHeight="54px"
                data-automation="user_roles_header"
            >
                <mat-panel-title>
                    <h4 class="card-title" data-automation="user_roles">{{ 'user.user_roles' | translate }}</h4>
                </mat-panel-title>
                <mat-panel-description class="user_roles_subtitle"> </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="form-group1">
                <ng-container>
                    <h4 class="form-header">{{ 'user.edit.location_roles' | translate }}</h4>
                    <app-location-roles
                        [mode]="EDIT"
                        [userId]="userId"
                        [hasEcrm]="hasEcrm"
                        [isOnboarding]="isOnboardingResource"
                        (applyingRolesEvent)="applyRoles($event)"
                        (onRoleAdd)="onRoleAdd()"
                    >
                    </app-location-roles>
                </ng-container>

                <!-- <h4 class="form-header-no-border">{{ 'user.edit.active_location_roles' | translate }}</h4> -->
                <div class="form-header-container">
                    <h4 class="form-header-no-border">{{ 'user.edit.active_location_roles' | translate }}</h4>
                    <span
                        ><span>{{ 'user.edit.roles_added' | translate }} {{ addedRoles }}</span
                        ><span class="ml-1">{{ 'user.edit.roles_deleted' | translate }} {{ removedRoles }}</span></span
                    >
                </div>
                <!-- <ng-container *ngIf="!businessRolesService.loading$ | async"> -->
                <app-active-location-roles
                    [mode]="EDIT"
                    [showControls]="true"
                    [changed]="userRolesHasChanged$ | async"
                    [hasEcrm]="ecrmId !== '' && ecrm_id !== null && ecrm_id !== undefined && ecrm_id.length == 18"
                    (applyingRolesEvent)="applyRoles($event)"
                    (onRoleRemoval)="onRoleRemoval($event)"
                    (unRemoveRoleEvent)="unRemoveRole($event)"
                    (onSiteRemoval)="onSiteRemoval($event)"
                    (onRoleUpdate)="newSubmitRoles()"
                ></app-active-location-roles>
                <!-- </ng-container> -->
                <!-- <ng-container *ngIf="businessRolesService.loading$ | async">
                    <mat-spinner *ngIf="loadingUserTypes" matSuffix diameter="23"></mat-spinner>
                </ng-container> -->
            </div>
        </mat-expansion-panel>

        <mat-expansion-panel
            togglePosition="before"
            data-automation="syncInfoSectionEdit"
            [expanded]="expandableSectionsState[2]"
            (opened)="expansionOpened(2)"
            (closed)="expansionClosed(2)"
            *ngIf="showSyncSection"
        >
            <mat-expansion-panel-header
                class="panel-override"
                collapsedHeight="54px"
                expandedHeight="54px"
                data-automation="sync_information_header"
            >
                <mat-panel-title>
                    <h4 class="card-title" data-automation="sync_information">
                        {{ 'user.sync_information' | translate }}
                    </h4>
                </mat-panel-title>
                <mat-panel-description class="sync_information_subtitle"> </mat-panel-description>
            </mat-expansion-panel-header>

            <!--- Sync to ECRM --->
            <div class="form-group">
                <div class="form-entry" *ngIf="isEditMode">
                    <!--- ECRM Contact ID --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.ecrm_contact_id' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="ecrmContactHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.ecrm_contact_id' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="contact_id"
                            type="text"
                            class="input-entry"
                            placeholder="{{ 'user.ecrm_contact_id' | translate }}"
                            formControlName="contact_id"
                            data-url-persist
                            data-automation="contact_id"
                            (input)="onECRMContactIDChange()"
                            matTooltip="{{ 'user.tooltip.ecrm_contact_id' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('contact_id').invalid &&
                                (userForm.get('contact_id').dirty || userForm.get('contact_id').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('contact_id').errors?.pattern">
                                <span data-automation="ecrmContactIDLengthError">
                                    {{ 'user.validation.ecrm.contact_id_length' | translate }}
                                </span>
                            </mat-error>
                            <mat-error *ngIf="userForm.get('contact_id').errors?.start">
                                <span data-automation="ecrmContactIDStartError">
                                    {{ 'user.validation.ecrm.contact_id_start' | translate }}
                                </span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry" *ngIf="isEditMode">
                    <!--- ECRM User ID --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.ecrm_id' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="ecrmUserHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.ecrm_user_id' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="ecrmID"
                            type="text"
                            class="input-entry"
                            placeholder="{{ 'user.ecrm_id' | translate }}"
                            formControlName="ecrm_id"
                            data-url-persist
                            data-automation="ecrmID"
                            (input)="onECRMUserIDChange()"
                            matTooltip="{{ 'user.tooltip.ecrm_user_id' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('ecrm_id').invalid &&
                                (userForm.get('ecrm_id').dirty || userForm.get('ecrm_id').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('ecrm_id').errors?.required">
                                <span>{{ 'user.validation.required' | translate }}</span>
                            </mat-error>
                            <mat-error *ngIf="userForm.get('ecrm_id').errors?.pattern">
                                <span data-automation="ecrmIDLengthError">
                                    {{ 'user.validation.ecrm.length' | translate }}
                                </span>
                            </mat-error>
                            <mat-error *ngIf="userForm.get('ecrm_id').errors?.start">
                                <span data-automation="ecrmIDStartError">
                                    {{ 'user.validation.ecrm.start' | translate }}
                                </span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div>
                    <div class="form-entry">
                        <div class="checkbox-feedback">
                            <span>
                                <mat-checkbox
                                    data-automation="ecrmSync"
                                    id="sync_to_ecrm"
                                    formControlName="sync_to_ecrm"
                                    (change)="handleEcrmChange()"
                                    matTooltip="{{ 'user.tooltip.sync_to_ecrm' | translate }}"
                                >
                                    {{ 'user.sync_to_ecrm' | translate }}
                                </mat-checkbox>
                                <mat-icon
                                    data-automation="syncToEcrmHelpIcon"
                                    class="tooltip-icon-checkbox"
                                    matTooltip="{{ 'user.tooltip.sync_to_ecrm' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </span>

                            <span *ngIf="userForm.get('last_sync_date').value" class="last-synced">
                                {{ 'user.last_sync_date' | translate }}
                                {{ userForm.get('last_sync_date').value | date : 'medium' }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="form-entry">
                    <!--- Country --->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.country' | translate }}</mat-label>
                        <mat-select
                            id="country_1"
                            (input)="onAddressFieldChange($event)"
                            class="input-entry create-select"
                            [formControl]="addressesFormArray.controls[0].get('country')"
                            data-automation="country_1"
                            (selectionChange)="onCountryChange($event)"
                            placeholder="{{
                                (addressFieldValidation
                                    ? 'user.create.placeholder.country_selector_required'
                                    : 'user.create.placeholder.country_selector'
                                ) | translate
                            }}"
                        >
                            <mat-option>{{ 'user.create.placeholder.country_selector' | translate }}</mat-option>
                            <mat-option *ngFor="let country of countries" [value]="country.name">{{
                                country.display_label
                            }}</mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                addressesFormArray.controls[0].get('country').invalid &&
                                (addressesFormArray.controls[0].get('country').dirty ||
                                    addressesFormArray.controls[0].get('country').touched)
                            "
                        >
                            <mat-error *ngIf="addressesFormArray.controls[0].get('country').errors?.required">
                                {{ 'user.validation.required' | translate }}
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>
            </div>
        </mat-expansion-panel>

        <mat-expansion-panel
            togglePosition="before"
            data-automation="user_settings_panel"
            *ngIf="showingUserPreferences"
            [expanded]="expandableSectionsState[3]"
            (opened)="expansionOpened(3)"
            (closed)="expansionClosed(3)"
        >
            <mat-expansion-panel-header
                class="panel-override"
                collapsedHeight="54px"
                expandedHeight="54px"
                data-automation="user_settings_header"
            >
                <mat-panel-title>
                    <h4 class="card-title" data-automation="user_settings">
                        {{ 'user.user_settings' | translate }}
                    </h4>
                </mat-panel-title>
                <mat-panel-description class="user_settings_subtitle"> </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="form-group">
                <div class="form-entry">
                    <div class="mat-form-field-container">
                        <!-- Platform Locale selector -->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.platform_locale' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="userPreferencesLocaleHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.user_preferences.locale' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="locale"
                                class="input-entry create-select"
                                formControlName="locale"
                                data-automation="localeSelector"
                            >
                                <mat-option
                                    *ngFor="let locale of multiLocaleConfig.supportedLocales"
                                    [value]="locale.localeName"
                                >
                                    {{ locale.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <!-- Timezone selector-->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.timezone' | translate }}</mat-label>
                            <mat-select
                                id="timezone"
                                class="input-entry create-select"
                                formControlName="timezone"
                                data-automation="timezoneSelector"
                            >
                                <mat-option *ngFor="let timezone of timezones" [value]="timezone.timezoneName">
                                    {{ timezone.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>

                <div class="form-entry">
                    <div class="mat-form-field-container">
                        <!-- System of measurement selector-->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.measurement' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="userPreferencesMeasurementHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.measurement' | translate }}"
                                >
                                    info_outline
                                </mat-icon>
                            </mat-label>
                            <mat-select
                                id="measurement"
                                class="input-entry create-select"
                                formControlName="unit_system"
                                data-automation="measurement_systemSelector"
                                placeholder="{{ 'user.create.placeholder.measurement_selector' | translate }}"
                                matTooltip="{{ 'user.tooltip.measurement' | translate }}"
                            >
                                <mat-option *ngFor="let measurement of measurements" [value]="measurement.name">
                                    {{ measurement.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                        <!-- Home Application selector-->
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>{{ 'user.create.application' | translate }}</mat-label>
                            <mat-label>
                                <mat-icon
                                    data-automation="userPreferencesHomeAppHelpIcon"
                                    class="tooltip-icon"
                                    matTooltip="{{ 'user.tooltip.home_application' | translate }}"
                                    >info_outline</mat-icon
                                >
                            </mat-label>
                            <mat-select
                                id="application"
                                class="input-entry create-select"
                                formControlName="home_application"
                                data-automation="home_applicationSelector"
                                placeholder="{{ 'user.create.placeholder.application_selector' | translate }}"
                                matTooltip="{{ 'user.tooltip.home_application' | translate }}"
                            >
                                <mat-option *ngFor="let application of applications" [value]="application.name">
                                    {{ application.displayLabel }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
            </div>
        </mat-expansion-panel>

        <mat-expansion-panel
            togglePosition="before"
            data-automation="api_keys_panel"
            *ngIf="isSystemUser()"
            [expanded]="expandableSectionsState[4]"
            (opened)="expansionOpened(4)"
            (closed)="expansionClosed(4)"
        >
            <mat-expansion-panel-header
                class="panel-override"
                collapsedHeight="54px"
                expandedHeight="54px"
                data-automation="api_keys_header"
            >
                <mat-panel-title>
                    <h4 class="card-title" data-automation="api_keys">
                        {{ 'user.api_keys' | translate }}
                    </h4>
                </mat-panel-title>
                <mat-panel-description class="api_keys_subtitle"> </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="form-group" data-automation="apiKeysSection">
                <div class="form-entry">
                    <!-- API Key ID (read-only in edit) -->
                    <mat-form-field appearance="outline" floatLabel="always" subscriptSizing="dynamic">
                        <mat-label>{{ 'user.api_key_id' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="apiKeyIdHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.api_key_id' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="api_key_id"
                            type="text"
                            class="input-entry"
                            data-automation="api_key_id"
                            formControlName="api_key_id"
                            data-url-persist
                            matTooltip="{{ 'user.tooltip.api_key_id' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('api_key_id').invalid &&
                                (userForm.get('api_key_id').dirty || userForm.get('api_key_id').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('api_key_id').errors?.required">
                                <span data-automation="apiKeyIdErrorRequired">{{
                                    'user.validation.required' | translate
                                }}</span>
                            </mat-error>
                            <mat-error *ngIf="userForm.get('api_key_id').errors?.pattern">
                                <span data-automation="apiKeyIdErrorPattern">{{
                                    'user.validation.api_key_id.pattern' | translate
                                }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="form-entry">
                    <!-- Display Label (read-only in edit) -->
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.key_display_label' | translate }}</mat-label>
                        <mat-label>
                            <mat-icon
                                data-automation="keyDisplayLabelHelpIcon"
                                class="tooltip-icon"
                                matTooltip="{{ 'user.tooltip.key_display_label' | translate }}"
                                >info_outline</mat-icon
                            >
                        </mat-label>
                        <input
                            matInput
                            id="key_display_label"
                            type="text"
                            class="input-entry"
                            data-automation="key_display_label"
                            formControlName="key_display_label"
                            data-url-persist
                            matTooltip="{{ 'user.tooltip.key_display_label' | translate }}"
                        />
                        <mat-error
                            *ngIf="
                                userForm.get('key_display_label').invalid &&
                                (userForm.get('key_display_label').dirty || userForm.get('key_display_label').touched)
                            "
                        >
                            <mat-error *ngIf="userForm.get('key_display_label').errors?.required">
                                <span data-automation="keyDisplayLabelErrorRequired">{{
                                    'user.validation.required' | translate
                                }}</span>
                            </mat-error>
                        </mat-error>
                    </mat-form-field>
                </div>

                <!-- API Key Token (visible once key exists) -->
                <div class="form-entry" *ngIf="apiKeyToken">
                    <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ 'user.api_key_token' | translate }}</mat-label>
                        <input
                            matInput
                            id="api_key_token"
                            type="text"
                            class="input-entry"
                            data-automation="api_key_token"
                            [value]="apiKeyToken"
                            readonly
                            disabled
                        />
                    </mat-form-field>
                </div>

                <!-- Generate API Key button (shown when no key exists yet and form not yet shown) -->
                <div class="form-entry" *ngIf="isSystemUser() && !apiKeyToken && !showApiKeyGenerateForm">
                    <button
                        data-automation="generateApiKeyButton"
                        class="icon-btn x-btn-primary"
                        [disabled]="isSaving"
                        (click)="onGenerateApiKeyClick()"
                    >
                        <mat-icon *ngIf="!isSaving">vpn_key</mat-icon>
                        <div *ngIf="isSaving" class="btn-spinner">
                            <mat-spinner diameter="15"></mat-spinner>
                        </div>
                        {{ 'user.generate_api_key' | translate }}
                    </button>
                </div>

                <!-- Save API Key button (shown after Generate is clicked and fields are enabled) -->
                <div class="form-entry" *ngIf="isSystemUser() && !apiKeyToken && showApiKeyGenerateForm">
                    <button
                        data-automation="saveApiKeyButton"
                        class="icon-btn x-btn-primary"
                        [disabled]="!userForm.get('api_key_id').valid || !userForm.get('key_display_label').valid || isSaving"
                        (click)="createApiKey()"
                    >
                        <mat-icon *ngIf="!isSaving">check</mat-icon>
                        <div *ngIf="isSaving" class="btn-spinner">
                            <mat-spinner diameter="15"></mat-spinner>
                        </div>
                        {{ 'user.create_api_key' | translate }}
                    </button>
                </div>
            </div>
        </mat-expansion-panel>
    </div>
</div>
